import $lodash from '@sparta.global.vendors/lodash';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaMessageUtility from '@sparta.global.utilities/sparta-messaging-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaUILayersUtility from '@sparta.global.utilities/sparta-ui-layers-utility/dist/es6.js';
import spartaValidationFramework from '@sparta.global.utilities/sparta-validation-framework-utility/dist/es6.js';
import $jq from '@sparta.global.vendors/jquery';

/** 
 * @class Logger
 * @classdesc Authub logging will taken care to manage. 
 */

class Logger {
  /**
   * @private
   * @function _isDebugEnabled
   * @description It will check whether the logs need to print or not
   * @returns {boolean}
   */
  _isDebugEnabled() {
    return true;
  }

  /**
   * @public
   * @function log
   * @description  It will validate the condition and print the console logging
   * @param  {...any} logMessage Will get the log details to be print
   */
  log(...logMessage) {
    //console.log('%cHello', 'color: green; background: yellow; font-size: 30px');
    if (this._isDebugEnabled()) {
      console.log('AuthHub Logs : ', ...logMessage);
    }
  }
  /**
   * @public
   * @function warn
   * @description  It will validate the condition and print the warning logging
   * @param  {...any} warnMessage Will get the log details to be print
   */
  warn(...warnMessage) {
    if (this._isDebugEnabled()) {
      console.warn('AuthHub Warning : ', ...warnMessage);
    }
  }

  /**
   * @public
   * @function error
   * @description  It will validate the condition and print the error logging
   * @param  {...any} errorMessage Will get the log details to be print
   */
  error(...errorMessage) {
    if (this._isDebugEnabled()) {
      console.error('AuthHub Error : ', ...errorMessage);
    }
  }

  /**
   * @private
   * @function trace
   * @description  It will print the trace when the log is getting triggered.
   */
  trace() {
    const traceLimit = 4;
    const traceList = new Error().stack?.split('\n')?.slice(3, traceLimit + 2)?.join('\n');
    const lastFunctionTrigger = new Error().stack?.split('\n')[4];
    const lastFunctionTriggerName = lastFunctionTrigger?.split('(')[0]?.trim();
    console.log('Function Triggered', lastFunctionTriggerName);
    console.log(`%c AuthHub Trace : ${traceList}`, 'color: white; background: #8d8d3eab; font-size: 14px');
  }
}

/**
 * @const LoggerUtil
 * @description single instance of Logger used to expose this utility
 */
const LoggerUtil = new Logger();

const MESSAGE_TYPE$2 = {
  SUCCESS: 'Success'
};

/**
     * @function childElementAvailabilityPromiseCheck
     * @description  Used observer to check if any child element is appened inside the given element, 
     *     It will return if already available or will wait some time if any element is appending.
     * @param {HTMLElement} element - Messaging element id/class to remove the messaging.
     * @param {boolean} [isTimeLimitCheck=true]  - If we need to wait for long then disable it 
     * @returns {Promise}
*/
function childElementAvailabilityPromiseCheck(element, isTimeLimitCheck = true) {
  return new Promise((resolve, reject) => {
    if (element.childElementCount > 0) {
      resolve();
    } else {
      let hasChanged = false;
      const REJECTION_TIME_LIMIT = 5000;
      const observerConfig = {
        childList: true
      };
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === "childList") {
            hasChanged = true;
            observer.disconnect();
            resolve();
          }
        });
      });
      if (isTimeLimitCheck) {
        window.setTimeout(() => {
          if (!hasChanged) {
            reject();
          }
        }, REJECTION_TIME_LIMIT);
      }
      observer.observe(element, observerConfig);
    }
  });
}

/**
 * @class Message
 * @classdesc Authub message util will handle the display messaging the module.
 */
class Message {
  constructor() {
    this.displayMessageUtilList = {};
    this.recentMessageTriggerThrottleList = {};
  }

  /**
  * @function displayPageLevelMessaging
  * @description  It will display the messaging in module level based on the element added.
  * @param {Object} messagingDetails - Object data to display the messaging.
  * @param {String} messagingDetails.elementId - Element ID/Class where the displaying message in target element.
  * @param {String} messagingDetails.title - Title/Header of the message if anything needed to display.
  * @param {String | Array} messagingDetails.message - Respective message to display, For list of messages use Array of Strings.
  * @param {String} messagingDetails.type - Type of the message (Success/Error/Info/Help/Warning).
  * @param {Object} messagingDetails.option - To update whether field need to disable or enable, on default its false.
  * @param {String} messagingDetails.option.size - size of the message to display ('large', 'medium', 'small', or 'xsmall').
  * @param {String} messagingDetails.option.listType - 'ordered', 'unordered', or 'paragraph'. Applicable if message contains an array of messages.
  * @param {String} messagingDetails.option.insert - 'before', 'after', or 'replace'. Controls positioning of message with respect to target element..
  * @param {Boolean} messagingDetails.option.dismiss - Adds an "X" button to message allowing for it to be closed.
  * 
  * @returns {undefined}
  */
  displayPageLevelMessaging({
    elementId,
    title = '',
    message = ' ',
    type = MESSAGE_TYPE$2.SUCCESS,
    option = {},
    dynamicContent
  }) {
    const messagingElement = document.querySelector(elementId);

    // Used Throttle method to prevent, same error message triggered multiple times in a second
    if (this.recentMessageTriggerThrottleList[elementId]) {
      return false;
    }
    this.hidePageLevelMessaging(elementId);
    if (!this.displayMessageUtilList[elementId]) {
      this.displayMessageUtilList[elementId] = new spartaMessageUtility();
    }
    if (!!messagingElement) {
      const messagingUtilInstance = this.displayMessageUtilList[elementId];
      this._removeDefaultAriaReading(messagingElement);
      // Initalize the messagedTriggered enable to wait for the completion.
      this.recentMessageTriggerThrottleList[elementId] = true;
      window.setTimeout(() => {
        delete this.recentMessageTriggerThrottleList[elementId];
      }, 1000);
      messagingUtilInstance[`show${type}MessageInsideFirst`](elementId, title, message, {
        ...option
      });
      childElementAvailabilityPromiseCheck(messagingElement).then(() => {
        const errorMessageElement = messagingElement.querySelector('.spartaMessage');
        const errorMessageTitleElement = document.querySelector('.spartaMessage .spartaMessage-title');
        this._removeIconContentFromMessage();
        const dynamicMessageElement = document.querySelector('#dynamic-content');
        if (!!dynamicMessageElement && !!dynamicContent) {
          document.querySelector('#dynamic-content').textContent = dynamicContent;
        }
        if (!!title) {
          errorMessageTitleElement?.focus();
        } else {
          errorMessageElement?.focus();
        }
      });
    } else {
      LoggerUtil.error('ElementID is mandatory to display the message content, Kindly check  whether you added the globalId for main modules');
    }
  }

  /**
    * @function hidePageLevelMessaging
    * @description  Hide the page level message which displayed based on the element.
    * @param {String} elementId - Messaging element id/class to remove the messaging.
    * @returns {undefined}
  */
  hidePageLevelMessaging(elementId) {
    if (this.displayMessageUtilList[elementId]) {
      this.displayMessageUtilList[elementId].destroy();
    }
  }

  /**
    * @function _removeDefaultAriaReading
    * @description  Removed the aria-live and role which is added in the Messaging parent element to avoid multiple reading in JAWS.
    * @param {HTMLElement} element - Messaging element to remove the attributes.
    * @returns {undefined}
  */
  _removeDefaultAriaReading(element) {
    element.removeAttribute('aria-live');
    element.removeAttribute('role');
  }

  /**
    * @function _removeIconContentFromMessage
    * @description  Removed the icon content from sparta message icon ADA text.
    * @param {HTMLElement} element - Messaging element to remove the attributes.
    * @returns {undefined}
  */
  _removeIconContentFromMessage() {
    const messageTitleElement = document.querySelector('.spartaMessage .spartaMessage-title');
    const messageIconElement = messageTitleElement ? document.querySelector('.spartaMessage .spartaMessage-title .ada-hidden') : document.querySelector('.spartaMessage .spartaMessage-text .ada-hidden');
    const spaceIndex = messageIconElement.textContent.indexOf(' ');
    messageIconElement.textContent = messageIconElement.textContent.substring(0, spaceIndex);
  }
}

/**
 * @const MessageUtil
 * @description single instance of Message used to expose this utility
 */
const MessageUtil = new Message();

const ERROR_CODE = {
  GENERAL_ERROR: "GENERAL_ERROR",
  MANDATORY_FIELD: "MISSING_MANDATORY_FIELD",
  UI_PAGE_ERROR: "UI_PAGE_LEVEL_ERROR"
};
const SPARTA_EVENTS = {
  SCREEN_NAVIGATION_INITATED: 'SCREEN_NAVIGATION_INITATED',
  SCREEN_NAVIGATION_COMPLETED: 'SCREEN_NAVIGATION_COMPLETED',
  ROUTING_COMPLETED: 'SCREEN_ROUTING_COMPLETED',
  NO_PREVIOUS_ROUTING: 'NO_PREVIOUS_SCREEN_ROUTING',
  NEXT_SCREEN: 'AH_NEXT_SCREEN',
  PREVIOUS_SCREEN: 'AH_PREVIOUS_SCREEN',
  API_RESPONSE: "AH_API_RESPONSE",
  PAGE_ERROR_FALLBACK: 'AH_PAGE_ERROR_FALLBACK',
  AH_INITIATE_QUICK_HELP: "AH_INITIATE_QUICK_HELP"
};
const STORE_KEY$2 = {
  WIDGET_CONFIG: 'AH_WIDGET_CONFIG_KEY',
  WIDGET_NAMESPACE: 'AH_WIDGET_NAMESPACE_KEY',
  SITE_CONFIG: 'AH_SITE_CONFIG_KEY',
  ENCRYPTION_KEY: 'AH_PUBLIC_KEY',
  WIDGET_INFO_KEY: 'AH_WIDGET_INFO',
  WOGW_KEY: 'AH_WOGW_KEY',
  SPA_PARAM: 'AH_SPA_PARAM',
  IS_V3: 'AH_V3_KEY',
  PUBLIC_KEY: 'AH_PUB_KEY'
};
const GLOBAL_ERROR_ID = 'ah-main-module-error-global';
const MESSAGE_TYPE$1 = {
  SUCCESS: 'Success',
  ERROR: 'Error',
  WARNING: 'Warning'
};

/** 
 *  STATUS_CODE are used in main module to check whether it is success or failure scenarios
 *  In FetchUtil both success & error status code consider as success scenario only
 */
const STATUS_CODE = {
  SUCCESS: 200,
  ERROR: 207
};
const ERROR_TYPE = {
  FATAL: 'fatal',
  CASCADE: 'cascade'
};
const WIDGET_TYPE = {
  PAGE: 'PAGE',
  MODAL: 'MODAL'
};

/**
* Filter rules to be added in payload for second factor linkage BOA client
*/
const BOA_FILTER_RULES = [{
  "name": "PLATFROM",
  "value": "CONSUMER"
}, {
  "name": "BRAND",
  "value": "BOA"
}, {
  "name": "CHANNEL",
  "value": "WEB"
}];
const SCREEN_SIZE = {
  X_SMALL: 414,
  SMALL: 767,
  MEDIUM: 1024
};
const ahConstantList = {
  ERROR_CODE,
  SPARTA_EVENTS,
  STORE_KEY: STORE_KEY$2,
  GLOBAL_ERROR_ID,
  MESSAGE_TYPE: MESSAGE_TYPE$1,
  STATUS_CODE,
  ERROR_TYPE,
  WIDGET_TYPE,
  BOA_FILTER_RULES,
  SCREEN_SIZE
};

const LoggerCheck = callbackFunction => function (...args) {
  try {
    return callbackFunction(...args);
  } catch (e) {
    LoggerUtil.error('Logging::', e);
  }
};

// Class constructor => ErrorLoggingMixin.wrapMethods(this);

/**
 * @class Events
 * @classdesc Authub Event util which handle the pub/sub along with some top layer conditionals
 */

const {
  STORE_KEY: STORE_KEY$1
} = ahConstantList;
let subscribedEventList = {};
class Events {
  constructor() {
    this.recentEventTriggered = {};
  }

  /**
   * 
   * @function _getCustomEventName 
   * @param {String} eventName 
   * @param {Boolean} isPreventNameSpace  It is used to prevent appending the namespace in eventName, 
   *                                      This usecase mainly used for Vue sites as sibiling modules
   * @returns String
   */
  _getCustomEventName(eventName, isPreventNameSpace) {
    if (typeof eventName !== 'string') {
      LoggerUtil.error('Event name is undefined, kindly provide the valid string to proceed.');
      return null;
    }
    const widgetNamespace = spartaStoreUtility.getData(STORE_KEY$1.WIDGET_NAMESPACE);
    const updatedEventName = eventName.replace(':', "__");
    const customEventName = !!widgetNamespace ? `${updatedEventName}:${widgetNamespace}` : updatedEventName;
    return !!isPreventNameSpace ? eventName : customEventName;
  }

  /**
   * @public
   * @function pub 
   * @description - Publish the event along with the param which passed 
   *                and prevent same event published in less than 1000ms 
   * @param  {...any} args It will pass the event name and the detail
   */
  pub(eventName, data, eventInfo = {}) {
    const {
      isPreventNameSpace
    } = eventInfo;
    const customEventName = this._getCustomEventName(eventName, isPreventNameSpace);
    const widgetInfoDetails = spartaStoreUtility.getData(STORE_KEY$1.WIDGET_INFO_KEY) || {};
    if (!this.recentEventTriggered[customEventName]) {
      this.recentEventTriggered[customEventName] = true;
      window.setTimeout(() => {
        delete this.recentEventTriggered[customEventName];
      }, 1000);
      LoggerUtil.log('Event Published', customEventName);
      // Logger.trace();
      spartaEventsUtility.pub(customEventName, data, {
        ...eventInfo,
        ...widgetInfoDetails
      });
    }
  }

  /**
   * @public
   * @function sub 
   * @description - It will subscribe the event along with the param which passed 
   * @param  {...any} args It will pass the event name and the detail
   */
  sub(eventName, callbackFunction, retroactive = false, eventInfo = {}) {
    const {
      isPreventNameSpace
    } = eventInfo;
    const customEventName = this._getCustomEventName(eventName, isPreventNameSpace);
    const widgetInfoDetails = spartaStoreUtility.getData(STORE_KEY$1.WIDGET_INFO_KEY) || {};
    LoggerUtil.log('Event Subscribed', customEventName);
    this._addEventList(customEventName);
    spartaEventsUtility.sub(customEventName, callbackFunction, retroactive, {
      ...eventInfo,
      ...widgetInfoDetails
    });
  }
  _addEventList(customEventName) {
    if (typeof customEventName !== 'string') {
      return null;
    }
    const [, namespace = 'default'] = customEventName.split(":");
    if (!subscribedEventList[namespace]) {
      subscribedEventList[namespace] = [];
    }
    subscribedEventList[namespace].push(customEventName);
  }
  unsubByNamespace(namespace = 'default') {
    if (subscribedEventList[namespace]) {
      subscribedEventList[namespace].forEach(element => {
        spartaEventsUtility.unsubByEvent(element);
      });
    }
  }

  /**
   * @public
   * @function unsubByEvent 
   * @description - Unsubscribe the Event which already done the subscribe
   * @param  {...any} args It will pass the event name and the detail
   */
  unsubByEvent(...args) {
    const [eventName, ...otherArgs] = args;
    const customEventName = this._getCustomEventName(eventName);
    LoggerUtil.log('Event UnSubscribed', customEventName);
    spartaEventsUtility.unsubByEvent(customEventName, ...otherArgs);
  }
}

/**
 * @const EventUtil
 * @description single instance of Events used to expose this utility
 */
const EventUtil = new Events();

const LINKAGE = {
  ML: 'Linkage-ML',
  BOL: 'Linkage-BOL',
  PB: 'Linkage-PB',
  BOA: 'Linkage-BOA'
};
const FORGET_ID = {
  BOL: "Forgot-BOL"
};
class clientCheck {
  _getClientParam(key) {
    this.clientParams = ahCommonUtility._fetchWidgetOrSiteConfigData();
    return this.clientParams[key];
  }
  isML() {
    return ahClientCheck$1._getClientParam("authType") === LINKAGE.ML;
  }
  isBOL() {
    return ahClientCheck$1._getClientParam("authType") === LINKAGE.BOL || ahClientCheck$1._getClientParam("authType") === FORGET_ID.BOL;
  }
  isPB() {
    return ahClientCheck$1._getClientParam("authType") === LINKAGE.PB;
  }
  isCAW() {
    return ahClientCheck$1._getClientParam("authType") === LINKAGE.BOA;
  }
  isOCALS() {
    return ahClientCheck$1._getClientParam("originatingFrom") === "OCA-LS";
  }
  isGWIM() {
    return ahClientCheck$1._getClientParam("authType") === LINKAGE.PB || ahClientCheck$1._getClientParam("authType") === LINKAGE.ML || ahClientCheck$1._getClientParam("authType") === LINKAGE.BOL;
  }
  isStandalone() {
    return ahClientCheck$1._getClientParam("isStandalone");
  }
  isGWIMClient() {
    return ahClientCheck$1._getClientParam("originatingFrom") === "GWIM";
  }
  isBOAClient() {
    return ahClientCheck$1._getClientParam("originatingFrom") === "BOA";
  }
  isMEClient() {
    return ahClientCheck$1._getClientParam("authType") === "Login-ME";
  }
  isMMClient() {
    return ahClientCheck$1._getClientParam("authType") === "Login-MM";
  }
}

// export the ClientCheck for use in modules and utilities
const ahClientCheck$1 = new clientCheck();

/**
 * Initialize the store data
 * @property {object}
 */
let finalStoreData = {};

/** Class manage the state management of the widget/site. 
 * @class StateManagement
 */
class StateManagement {
  /**
  * @function getStoreData
  * @description  It will helps to get the store object fully or needed key specific.
  * @param {string} moduleKey - key which we need to use for the retrieve the Object.
  * @returns {object} - It will return the store object
  */
  getStoreData(moduleKey) {
    return !!moduleKey ? finalStoreData[moduleKey] : finalStoreData;
  }

  /**
  * @function addStoreData
  * @description  It will store the data which passed along with the exisiting store information
  * @param {Object} storeData - Module provided store information.
  * @returns {undefined}
  */
  addStoreData(storeData) {
    //const updatedStoreData = { ...finalStoreData, ...storeData };
    const updatedStoreData = $lodash.merge(finalStoreData, storeData);
    finalStoreData = $lodash.cloneDeep(updatedStoreData);
  }

  /**
  * @function updateStoreData
  * @description  It will update the store data with exisiting store information
  * @param {Object} storeData - Module provided store information.
  * @returns {undefined}
  */
  updateStoreData(storeData) {
    const newStoreData = {
      ...finalStoreData,
      ...storeData
    };
    finalStoreData = $lodash.cloneDeep(newStoreData);
  }

  /**
  * @function resetStoreData
  * @description  It will reset the store data.
  * @returns {undefined}
  */
  resetStoreData() {
    finalStoreData = {};
  }
}
/**
 * @const StateManagementUtil
 * @description single instance of StateManagement used to expose this utility
 */

const StateManagementUtil = new StateManagement();

/**
 * @const MODEL_DEFAULT_CONFIG
 * @description Default config of a modal
 */
const MODEL_DEFAULT_CONFIG = {
  type: 'modal',
  width: '500'
};

/**
 * @const DRAWER_DEFAULT_CONFIG
 * @description Default config of a drawer
 */
const DRAWER_DEFAULT_CONFIG = {
  type: 'drawer',
  //width: '500',
  size: 'small',
  closeButton: true,
  closeOnEscape: false,
  // Whether to close the modal using Esc key.
  closeOnClickOutside: false
};

/**
 * @const EVENTS
 * @description Events which is used in the uiLayer Utility
 */
const EVENTS$1 = {
  MODAL_OPENED: 'MODAL_OPENED.spartaUILayersUtility',
  MODAL_CLOSED: 'MODAL_CLOSE.spartaUILayersUtility',
  HEADER_UPDATE: 'MODAL_HEADER_UPDATE',
  NEXT_SCREEN_DISPLAY: 'SCREEN_NAVIGATION_COMPLETED' // It will do the automatic header/footer update based on the next screen navigation
};

/*
    Model event will handle and make sure only respective modal close or open will return to callback 
    not all modal trigger will callback to this event, to avoid un-necessary multiple event trigger
*/

/**
 * @class UILayer
 * @classdesc UI Layer will helps to display the Modal/Drawer and do the header and footer update
 */
class UILayer {
  constructor() {
    this._subscribeEvents();
    this._callbackList = {};
  }

  /**
   * 
   * A Config to be passed for the modal/drawer to be added
   * 
   * @typedef {object} ModalConfig
   * @property {string} elementId - Element Id which we need to fetch and display in the Modal
   * @property {string} widgetId - Widget Id will take from `this.settings.widgetInfo.id` - it will available in Module
   */

  /**
   * @public
   * @function displayModal
   * @description Display the Modal based on the config which provided
   * @param {Object} modalInfo
   * @param {ModalConfig} modalInfo.config {@link ModalConfig} to be used with config
   * @param {function} modalInfo.onLoad - Callback function when modal is loaded and displayed in screen.
   * @param {function} modalInfo.onClose - Callback funciton when modal is closed
   */
  displayModal({
    config = {},
    onLoad,
    onClose
  }) {
    this._callbackList[config.elementId] = {
      onLoad,
      onClose
    };
    this._displayUI({
      ...config,
      ...MODEL_DEFAULT_CONFIG
    });
  }

  /**
   * @public
   * @function displayModal
   * @description Display the Modal based on the config which provided
   * @param {object} drawerInfo
   * @param {ModalConfig} drawerInfo.config {@link ModalConfig} to be used with config
   * @param {function} drawerInfo.onLoad - Callback function when modal is loaded and displayed in screen.
   * @param {function} drawerInfo.onClose - Callback funciton when modal is closed
   */
  displayDrawer({
    config = {},
    onLoad,
    onClose
  }) {
    this._callbackList[config.elementId] = {
      onLoad,
      onClose
    };
    this._displayUI({
      ...config,
      ...DRAWER_DEFAULT_CONFIG
    });
  }

  /**
   * Hide the Displayed Modal
   * @param {string} elementId  - Element Id which used for show the Modal
   */
  hideModal(elementId) {
    spartaUILayersUtility.closeLayer(elementId);
  }

  /**
   * Hide the Displayed Drawer
   * @param {string} elementId  - Element Id which used for show the Drawer
   * @public
   */
  hideDrawer(elementId) {
    spartaUILayersUtility.closeLayer(elementId);
  }

  /**
   * It will helps to display the Modal/Drawer in screen
   * @param {object} option 
   * @private 
   */
  _displayUI(option) {
    const {
      elementId,
      widgetId,
      ...otherConfig
    } = option;
    const elementForModalBody = document.getElementById(elementId);
    if (!!elementForModalBody) {
      elementForModalBody.setAttribute('data-options', JSON.stringify(otherConfig));
      spartaUILayersUtility.openLayer(elementForModalBody, {}, null, widgetId);
    } else {
      console.error('Element is not there');
    }
  }

  /**
   *  It will subscribe the events which we need to capture
   * @private
   */
  _subscribeEvents() {
    EventUtil.sub(EVENTS$1.MODAL_OPENED, this._triggerModalLoaded.bind(this));
    EventUtil.sub(EVENTS$1.MODAL_CLOSED, this._triggerModalClosed.bind(this));
    EventUtil.sub(EVENTS$1.NEXT_SCREEN_DISPLAY, this._nextScreenDisplay.bind(this));
    EventUtil.sub(EVENTS$1.HEADER_UPDATE, this._nextScreenDisplay.bind(this));
  }

  /**
   * It will trigger when the Modal/Drwer load event is triggered
   * Header/Footer update will happen once the modal is getting triggered
   * @param {object} event - Event triggered details
   * @private
   */
  _triggerModalLoaded(event) {
    window.setTimeout(() => {
      const {
        contentId,
        layer,
        type
      } = event.data;
      if (this._callbackList[contentId]) {
        const {
          onLoad
        } = this._callbackList[contentId];
        this.modalLayerId = layer[0].id;
        this.contentId = contentId;
        this.layerType = type;
        this._headerUpdate({
          isOnLoad: true
        });
        this._footerUpdate();
        !!onLoad && onLoad();
      }
    });
  }

  /**
   * It will trigger when the Modal/Drawer close event is triggered
   * It will be resetted the value
   * @param {object} event - Event triggered details
   * @private
   */
  _triggerModalClosed(event) {
    const {
      contentId
    } = event.data;
    if (this._callbackList[contentId]) {
      const {
        onClose
      } = this._callbackList[contentId];
      this._resetModalContent();
      !!onClose && onClose();
    }
  }

  /**
   * Reset the class params assign during the Modal/Drawer load
   * @private
   */
  _resetModalContent() {
    delete this._callbackList[this.contentId];
    this.modalLayerId = "";
    this.contentId = "";
    this.layerType = "";
  }

  /**
   * When Next/Previous screen is navigated, it will helps to update the header
   * @todo - Update will happen only when the Modal/Drawer is opened not for all time
   * @private
   */
  _nextScreenDisplay({
    data
  } = {}) {
    this._headerUpdate({
      moduleId: data?.moduleId
    });
  }

  /**
   * It will get the displayed Section header element
   * @private
   * @returns {HTMLElement | null} 
   */
  _getDisplayHeaderElement(moduleId) {
    if (!!moduleId) {
      const headerElement = document.querySelector(`#${moduleId} [display-role-heading]`);
      return headerElement || null;
    } else {
      const contentElement = document.querySelector(`#${this.contentId}`);
      const sectionElementList = contentElement?.querySelectorAll('section.module-container');
      const visibleElement = ahCommonUtility.fetchVisibleElement(sectionElementList);
      return visibleElement.length > 0 ? visibleElement[0]?.querySelector('[display-role-heading]') : null;
    }
  }

  /**
   * It will get the displayed Section footer element
   * @private
   * @returns {HTMLElement | null} 
   */
  _getDisplayFooterElement() {
    const contentElement = document.querySelector(`#${this.contentId}`);
    const sectionElementList = contentElement.querySelectorAll('section.module-container');
    const visibleElement = ahCommonUtility.fetchVisibleElement(sectionElementList);
    return visibleElement.length > 0 ? visibleElement[0].querySelector('[display-role-footer]') : null;
  }

  /**
   * Modal/Drawer Header will updated and do the focus based on the section display
   * @param {{isOnLoad : boolean}} updateParam 
   * @private
   */
  _headerUpdate({
    isOnLoad,
    moduleId
  } = {}) {
    const headingElement = this._getDisplayHeaderElement(moduleId);
    if (!!headingElement) {
      const headingContent = headingElement.innerHTML;
      headingElement.classList.add('hide');
      spartaUILayersUtility.setModalTitle(this.modalLayerId, headingContent);
      document.querySelector('.spa-card__title').setAttribute('tabindex', '0');
      if (!isOnLoad) {
        window.setTimeout(() => {
          document.querySelector('.spa-card__title').focus();
        }, 100);
      }
    }
  }

  /**
   * Drawer Footer will updated and do the footer sticky based on the section display
   * @private
   */
  _footerUpdate() {
    const footerElement = this._getDisplayFooterElement();
    if (this.layerType === 'drawer' && footerElement) {
      footerElement.style.marginBottom = '16px';
      footerElement.style.bottom = '0';
      footerElement.style.position = 'fixed';
    }
  }
}
const UILayerUtility = new UILayer();

/** 
 * @class RouteNavigation
 * @classdesc will helps to routing the modules based on the requirement. 
 */

const EVENTS = {
  SCREEN_NAVIGATION_INITATED: 'SCREEN_NAVIGATION_INITATED',
  SCREEN_NAVIGATION_COMPLETED: 'SCREEN_NAVIGATION_COMPLETED',
  ROUTING_COMPLETED: 'SCREEN_ROUTING_COMPLETED',
  NO_PREVIOUS_ROUTING: 'NO_PREVIOUS_SCREEN_ROUTING'
};
class RouteNavigation {
  constructor() {
    this.routerModuleList = [];
    this.currentScreenObject = {};
    this.routerUUID = Math.random().toString(36).slice(2); // Random number generator to make identify the specific routing in page.
  }

  //TODO: setRouterList function need to add instead of setting from routerList directly.

  set routerList(list) {
    if (Array.isArray(list)) {
      // TODO : Need to avoid shallow clone, update it to deep clone for the pointer issues.
      this.routerModuleList = list;
      this.routerModuleListLength = this.routerModuleList.length;
      this._hideAllRouteScreen();
      /* Reset that to make sure if navigate to parent module and return back to child routing 
          it need to be fresh routing not to consider first screen is exisiting */
      this.currentScreenObject = {};
    } else {
      ahLoggerUtil.error('RouterModuleList need an Array of String');
    }
  }

  /**
   * @public
   * @function updateRouterList
   * @description  It will overide or add the router module in the specifc location.
   * @param {String} index Specific index of the array to update
   * @param {object} routerObject module object with `data` & `moduleId` need to be provided
   * @returns {undefined}
   */
  updateRouterList(index, routerObject = {}) {
    this.routerModuleList[index] = {
      ...routerObject
    };
  }

  /**
   * @public
   * @function navigateNextScreen
   * @description  It will make the next screen to display and hide the current screen.
   * @param {object} data Client provided data will passed to the next screen.
   * @returns {undefined}
   */
  navigateNextScreen(data) {
    const currentScreenIndex = this._getCurrentScreenRouteIndex();
    if (currentScreenIndex + 1 !== this.routerModuleListLength) {
      this._displayScreen(currentScreenIndex + 1, data);
    } else {
      ahEventsUtil.pub(`${EVENTS.ROUTING_COMPLETED}:${this.routerUUID}`, data);
    }
  }

  /** 
   * @public
   * @function navigatePreviousScreen
   * @description  It will make the previous screen to display and hide the current screen.
   * @param {object} data Client provided data will passed to the previous screen.
   * @returns {undefined}
   */
  navigatePreviousScreen(data) {
    const currentScreenIndex = this._getCurrentScreenRouteIndex();
    if (currentScreenIndex > 0) {
      this._displayScreen(currentScreenIndex - 1, data);
    } else {
      ahEventsUtil.pub(`${EVENTS.NO_PREVIOUS_ROUTING}:${this.routerUUID}`, data);
    }
  }

  /** 
   * @public
   * @function updateCurrentScreen
   * @description  It will update current screen to display the updated content.
   * @param {object} data Client provided data will passed to the current screen.
   * @returns {undefined}
  */
  updateCurrentScreen(data) {
    const currentScreenIndex = this._getCurrentScreenRouteIndex();
    this._displayScreen(currentScreenIndex, data);
  }

  /** 
   * @public
   * @function navigateTo
   * @description  It will help to navigate to respective screen when user provided the moduleId.
   * @param {string} moduleRouteName moduleId which registered in the routerList for navigation.
   * @param {object} data Client provided data will passed to the navigating screen.
   * @returns {undefined}
   */
  navigateTo(moduleRouteName, data) {
    const moduleRoutingIndex = this.routerModuleList.indexOf(moduleRouteName);
    if (moduleRoutingIndex > 1) {
      this._displayScreen(moduleRoutingIndex, data);
    } else {
      ahLoggerUtil.error(`${moduleRouteName} is not available in router List`);
    }
  }

  /**
   * @private
   * @function _displayScreen
   * @description  Display the module section based on the module index.
   *               It will trigger the Events before the Module getting displayed and after got displayed.
   *               `SCREEN_NAVIGATION_INITATED:MODULE_ID` along with the corresponding routing param in routing List
   *               `SCREEN_NAVIGATION_COMPLETED:MODULE_ID`
   * @param {string} displayIndex - displaying screen routing list index.
   * @param {object} data - Client provided data will passed to the displaying screen along with the default param specified.
   * @returns {undefined}
   */
  _displayScreen(displayIndex, data) {
    this.currentScreenObject = this.routerModuleList[displayIndex];
    if (!this.currentScreenObject) {
      ahLoggerUtil.error(`Your trying to route to the module without having any Routing List. Kindly check whether the routing list is included in RouteNavigation`);
    }
    const {
      moduleId,
      param
    } = this.routerModuleList[displayIndex];
    this._hideAllRouteScreen();
    const displayScreenElement = document.getElementById(moduleId);
    if (!!displayScreenElement) {
      ahEventsUtil.pub(`${EVENTS.SCREEN_NAVIGATION_INITATED}:${moduleId}`, {
        ...param,
        ...data
      });
      window.setTimeout(() => {
        displayScreenElement.classList.remove('hide');
        ahEventsUtil.pub(`${EVENTS.SCREEN_NAVIGATION_COMPLETED}:${moduleId}`);
        this._focusModuleHeader(displayScreenElement);

        //Temporarily added the aria live removal in input field error message element
        ahCommonUtility._removeAriaLiveInInputError(displayScreenElement);
      });
    } else {
      ahLoggerUtil.error(`Display Screen Element ${moduleId} is not available in Routing List`);
    }
  }

  /**
   * @private
   * @function _focusModuleHeader
   * @description  Focus the displayed module header if the `display-role-heading` attribute is present.
   * @returns {undefined}
   */
  _focusModuleHeader(displayScreenElement) {
    const headingElement = displayScreenElement.querySelector('[display-role-heading]');
    if (!!headingElement) {
      // Added Tabindex to avoid the focus missed out
      headingElement.tabIndex = '-1';
      ahCommonUtility.scrolltoFocusableElementView(headingElement);
    }
  }

  /**
   * @private
   * @function _hideAllRouteScreen
   * @description  Hide all the module section which is added for the routing.
   * @returns {undefined}
   */
  _hideAllRouteScreen() {
    [...this.routerModuleList].forEach(moduleObject => {
      const routingScreenId = moduleObject?.moduleId;
      const routingScreenElement = document.getElementById(routingScreenId);
      if (!!routingScreenElement) {
        routingScreenElement.classList.add('hide');
      } else {
        ahLoggerUtil.error(`Section ${routingScreenId} element is not available in the routing List`);
      }
    });
  }

  /**
    * @function _getCurrentScreenRouteIndex
    * @description  It will fetch the current display module routing list order Index.
    * @returns {number} - It will return the index of the module routing order
    */
  _getCurrentScreenRouteIndex() {
    const currentScreen = this.currentScreenObject?.moduleId;
    return [...this.routerModuleList].findIndex(moduleObject => currentScreen === moduleObject?.moduleId);
  }
}
class ahRouter {
  constructor({
    routes
  }) {
    this.routerList = [...routes];
    this.routerUUID = Math.random().toString(36).slice(2);
    this.currentScreen = {};
  }
  navigateTo(routingInfo) {
    const navigateRoutingDetail = this._getRouterdetail(routingInfo);
    this._hideAllRouteScreen();
    if (navigateRoutingDetail?.moduleId) {
      this._displayScreen(navigateRoutingDetail);
      this.currentScreen = {
        ...navigateRoutingDetail
      };
    } else {
      ahLoggerUtil.error(`${routingInfo} is not available in router List`);
    }
  }
  _displayScreen(navigateRoutingDetail) {
    const {
      moduleId,
      param
    } = navigateRoutingDetail;
    const displayScreenElement = document.getElementById(moduleId);
    if (!!displayScreenElement) {
      ahEventsUtil.pub(`${EVENTS.SCREEN_NAVIGATION_INITATED}:${moduleId}`, {
        ...param
      });
      window.setTimeout(() => {
        displayScreenElement.classList.remove('hide');
        ahEventsUtil.pub(`${EVENTS.SCREEN_NAVIGATION_COMPLETED}:${moduleId}`);
        this._focusModuleHeader(displayScreenElement);

        //Temporarily added the aria live removal in input field error message element
        ahCommonUtility._removeAriaLiveInInputError(displayScreenElement);
      });
    } else {
      ahLoggerUtil.error(`Display Screen Element ${moduleId} is not available in Routing List`);
    }
  }
  _hideAllRouteScreen() {
    [...this.routerList].forEach(moduleObject => {
      const routingScreenId = moduleObject?.moduleId;
      const routingScreenElement = document.getElementById(routingScreenId);
      if (!!routingScreenElement) {
        routingScreenElement.classList.add('hide');
      } else {
        ahLoggerUtil.error(`Section ${routingScreenId} element is not available in the routing List`);
      }
    });
  }
  _getRouterdetail(routingInfo) {
    let routingName, routingParam, navigateRoutingInfo;
    if (typeof routingInfo === 'string') {
      routingName = routingInfo;
    } else {
      routingName = routingInfo.name;
      routingParam = routingInfo.param;
    }
    navigateRoutingInfo = this.routerList.find(val => val.name === routingName);
    return {
      name: navigateRoutingInfo?.name,
      moduleId: navigateRoutingInfo?.moduleId,
      param: routingParam || navigateRoutingInfo?.param || {}
    };
  }
  _focusModuleHeader(displayScreenElement) {
    const headingElement = displayScreenElement.querySelector('[display-role-heading]');
    if (!!headingElement) {
      // Added Tabindex to avoid the focus missed out
      headingElement.tabIndex = '-1';
      ahCommonUtility.scrolltoFocusableElementView(headingElement);
    }
  }
}

/** Class manage Form validation support. 
 * @class StateManagement
 */

class UIFramework {
  constructor() {}

  /**
    * @function toggleEnableDisableFormFields
    * @description  It will disable/enable the input and select fields and button from the given parent formelemnt.
    * @param {Object} formDetails - Object data to disable/enable the form.
    * @param {Object} formDetails.formElement - The name of the custom event.
    * @param {Boolean} formDetails.isDisabled - To update whether field need to disable or enable, on default its false.
    * @returns {undefined}
  */
  toggleEnableDisableFormFields({
    formElement,
    isDisabled = false
  }) {
    const inputElementList = formElement.querySelectorAll('input');
    const selectElementList = formElement.querySelectorAll('select');
    const buttonElementList = formElement.querySelectorAll('button');
    const formInputFields = [...inputElementList, ...selectElementList, ...buttonElementList];
    formInputFields.forEach(fieldElement => {
      fieldElement.disabled = isDisabled;
    });
  }

  /**
    * @function _triggerFormValidators
    * @description  It will trigger the required and mandatory rules.
    * @param {HTMLElement} formElement - Object data to disable/enable the form.
    * @returns {undefined}
  */
  _triggerFormValidators(formElement) {
    const inputFieldList = $jq(formElement).find('[required]:visible');
    if (!spartaValidationFramework.fn) {
      spartaValidationFramework.init();
    }
    spartaValidationFramework.fn.core.validateRequiredFields(inputFieldList);
  }

  /**
   * @function formErrorValidationCheck
   * @description It will check the form fields and provide whether required or validation rule is valid
   * @param {HTMLFormElement} formElement 
   * @returns {{ isRequiredErrorAvailable : boolean, isValidationErrorAvailable : boolean }}
   */
  formErrorValidationCheck(formElement) {
    this._triggerFormValidators(formElement);
    const {
      requiredFieldIdList,
      validationFieldIdList
    } = this.fetchErrorInputElementIds(formElement);
    const isValidationErrorAvailable = requiredFieldIdList.length > 0;
    const isRequiredErrorAvailable = validationFieldIdList.length > 0;
    return {
      isRequiredErrorAvailable,
      isValidationErrorAvailable
    };
  }

  /**
   * @function fetchErrorInputElementIds
   * @description It will check the form fields and provide the inputfield Ids which required or rule based error occured
   * @param {HTMLFormElement} formElement 
   * @returns {{ requiredFieldIdList : boolean, validationFieldIdList : boolean }}
   */
  fetchErrorInputElementIds(formElement) {
    const formValidationErrorElements = formElement.querySelectorAll('.spa-error-form-input--validation');
    const formRequiredErrorElements = formElement.querySelectorAll('.spa-error-form-input--required');
    const requiredFieldIdList = [...formValidationErrorElements].map(element => element.id);
    const validationFieldIdList = [...formRequiredErrorElements].map(element => element.id);
    return {
      requiredFieldIdList,
      validationFieldIdList
    };
  }

  /**
   * @function isFormValid
   * @description Based on given form it will check whether the required and field validation rules satisfy
   * @param {HTMLFormElement} formElement Contains the Form Element 
   * @returns {boolean}
   */
  isFormValid(formElement) {
    const {
      isValidationErrorAvailable,
      isRequiredErrorAvailable
    } = this.formErrorValidationCheck(formElement);
    return !isValidationErrorAvailable && !isRequiredErrorAvailable;
  }

  /**
   * @function clearFormFieldError
   * @description It will clear the form field if have any errors displayed
   * @param {HTMLFormElement} formElement Contains the Form Element 
   * @returns {undefined}
   */
  clearFormFieldError(formElement) {
    const formInputFieldList = $jq(formElement).find('input:visible, select:visible');
    const DUMMY_VALIDATION_RULE_NAME = 'None';
    [...formInputFieldList].forEach(inputFieldElement => {
      spartaValidationFramework.fn.core.clearFieldError($jq(inputFieldElement), DUMMY_VALIDATION_RULE_NAME);
      spartaValidationFramework.fn.core.clearRequiredError($jq(inputFieldElement), DUMMY_VALIDATION_RULE_NAME);
    });
  }

  /**
   * @function resetFormField
   * @description It will reset the form field and if have any errors displayed
   * @param {HTMLFormElement} formElement Contains the Form Element 
   * @returns {undefined}
   */
  resetFormField(formElement) {
    const formInputFieldList = $jq(formElement).find('input:visible, select:visible');
    [...formInputFieldList].forEach(inputFieldElement => {
      this.resetSpecificFormInputField(inputFieldElement);
    });
    this.clearFormFieldError(formElement);
  }

  /**
   * @function resetSpecificFormInputField
   * @description It will reset the specific InputField and the rules to make it default
   * @param {HTMLInputElement} inputFieldElement Contains the Input Element 
   * @returns {undefined}
   */
  resetSpecificFormInputField(inputElement) {
    if (inputElement.type === 'radio' || inputElement.type === 'checkbox') {
      inputElement.checked = false;
    } else {
      inputElement.value = '';
    }
  }

  /**
   * @function validateSpecificFormInputField
   * @description It will validate the specific input field aligned with all rules
   * @param {HTMLInputElement} inputFieldElement Contains the Input Element 
   * @returns {undefined}
   */
  validateSpecificFormInputField(inputFieldElement) {
    spartaValidationFramework.fn.core.validateAllFieldRules($jq(inputFieldElement));
  }

  /**
   * @function displayCustomFieldError
   * @description It will show the specific input field error
   * @param {HTMLInputElement} inputFieldElement Contains the Input Element 
   * @returns {undefined}
   */
  displayCustomFieldError({
    inputFieldElement,
    validationRule = true,
    errorMessage
  }) {
    // 1000 ms is added to make sure that page level error message is dispalyed and focused before the field level is focused.l
    window.setTimeout(() => {
      spartaValidationFramework.fn.core.handleFieldError($jq(inputFieldElement), validationRule, errorMessage);
      document.getElementById(`${inputFieldElement.id}${'_errorMessage'}`).innerHTML = errorMessage;
    }, 1000);
  }

  /**
   * @function collectDivFormInputData
   * @description It will fetch the input field data and passed as object of response
   * @param {string} divElementId - Div elementId
   * @returns {object} - input name as key and their value
  */
  collectDivFormInputData(divElementId) {
    const divElement = document.getElementById(divElementId);
    const inputElement = divElement.querySelectorAll('input');
    const dropdownElement = divElement.querySelectorAll('select');
    let data = {};
    const handleRadioButtons = (radioName, value) => {
      if (!data[radioName]) {
        data[radioName] = value;
      }
    };

    /** If single Checkbox, it will return the value.
     *  If checkbox group, it will return as array of value.
     */
    const handleCheckboxes = (checkboxName, value) => {
      if (typeof data[checkboxName] === 'undefined') {
        const inputValue = value === 'true' ? true : value === 'false' ? false : value;
        data[checkboxName] = inputValue;
      } else if (!Array.isArray(data[checkboxName])) {
        data[checkboxName] = [data[checkboxName], value];
      } else {
        data[checkboxName].push(value);
      }
    };
    inputElement.forEach(input => {
      if (input.name) {
        if (input.type === 'radio') {
          input.checked && handleRadioButtons(input.name, input.value);
        } else if (input.type === 'checkbox') {
          input.checked && handleCheckboxes(input.name, input.value);
        } else {
          data[input.name] = input.value;
        }
      }
    });
    dropdownElement.forEach(ele => {
      data[ele.name] = ele.value;
    });
    return data;
  }
}
/**
 * @const uiFrameworkUtil
 * @description single instance of UIFramework used to expose this utility
 */

const uiFrameworkUtil = new UIFramework();

/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["content"] }] */
/* global bactm_captureCustomError */

/**
 * @name        Ah Commons Utility
 * @file        ah-common-utility.js
 * @description common methods for CAW
 * @classdesc   common methods for CAW
 * @global      Creates a global AhCommonUtility object
 * @author      zkos9ab
 */

const {
  MESSAGE_TYPE,
  STORE_KEY
} = ahConstantList;
class AhCommonUtility {
  constructor(options) {
    this.options = options || {};
    this.displayMessageUtilList = {};
    // As we facing issue for page modules if we import onload, so doing in lazyload
    import('@sparta.global.vendors/jsencrypt').then(JSEncrypt => {
      this.JSEncrypt = new JSEncrypt.default();
    });
  }

  /**
    * @function getBasePath
    * @description  It will generate the basePath in order of Client Domain if provided or spaParam endpoint or fallback(host origin)
    * @param {Object} spaParams - Widget/Site level this.spaParams.
    * @returns {String} - It will return the basedpath for API calls which took from widget/site config
  */
  getBasePath(spaParams) {
    let {
      domain
    } = this._fetchWidgetOrSiteConfigData();
    const fallbackPath = window.location.origin;
    if (ahClientCheck$1.isMEClient()) {
      domain = $lodash.get(spaParams, 'siteParams.meServiceEndpoint');
    } else if (ahClientCheck$1.isMMClient()) {
      domain = $lodash.get(spaParams, 'siteParams.mmServiceEndpoint');
    }
    return domain || $lodash.get(spaParams, 'siteParams.ahServiceEndpoint') || $lodash.get(spaParams, 'siteParams.serviceEndpoint') || $lodash.get(spaParams, 'siteParams.domain.hostname') || fallbackPath;
  }

  /**
    * @function getEndPoints
    * @description  It combine the basepath and the API urls list based on params.
    *               
    * @param {object} endpointList - List of API urls relative paths.
    * @param {String} basePath - Basepath to append in the API urls.
    * @returns {object}
  */
  getEndPoints(endpointList = {}, basePath) {
    let apiEndPoints = {};
    const defaultKeyList = Object.keys(endpointList).filter(val => !val.split('_')[1]);
    const widgetConfig = this.fetchWidgetConfigData();
    const siteConfig = this.fetchSiteConfigData();
    const suffix = spartaStoreUtility.getData(STORE_KEY.WOGW_KEY) || widgetConfig['endpointKey'] || siteConfig['endpointKey'] || spartaStoreUtility.getData('ENDPOINT_KEY');
    defaultKeyList.forEach(key => {
      const relativePathEndpoint = !!endpointList[`${key}_${suffix}`] ? endpointList[`${key}_${suffix}`] : endpointList[key];
      apiEndPoints[key] = basePath + relativePathEndpoint;
    });
    return apiEndPoints;
  }

  /**
    * @function includeExternalResource
    * @description  It helps to include
    * @param {Object} externalResourceObject - external resource object param.
    * @param {String} type - which resouece injection (style/script).
    * @param {String} path - External Resource path.
    * @param {Function} callback - Function to trigger when the external resource is loaded/failed.
    * @param {Boolean} triggerCallbackOnFailure - Boolean to notify if resource load is failed whether callback need to trigger or not.
    * @returns {undefined}
  */
  includeExternalResource({
    type,
    path,
    callback,
    triggerCallbackOnFailure = false
  }) {
    LoggerUtil.log(`Loading :  ${path}`);
    let element;
    if (type === 'script') {
      element = document.createElement('script');
      element.setAttribute('src', path);
      element.setAttribute('async', 'true');
    } else if (type === 'style') {
      element = document.createElement('link');
      element.setAttribute('rel', 'stylesheet');
      element.setAttribute('type', 'text/css');
      element.setAttribute('href', path);
      element.setAttribute('async', 'true');
    } else {
      LoggerUtil.log(`Not loading. Unknown TYPE: ${type}`);
      return;
    }
    element.onreadystatechange = element.onload = function () {
      if (callback && !callback.done && (!element.readyState || /loaded|complete/.test(element.readyState))) {
        callback.done = true;
        callback();
      }
    };
    element.onerror = function () {
      if (triggerCallbackOnFailure === true && callback && !callback.done) {
        LoggerUtil.log(`Unable to load: ${path}. Still triggering callback.`);
        callback.done = true;
        callback();
      } else {
        LoggerUtil.log(`Unable to load: ${path}. Callback not triggered.`);
      }
    };
    const head = document.getElementsByTagName('head')[0] || document.body;
    head.appendChild(element);
  }

  /**
    * @function trackCoreMetricesError
    * @description  Core Metrics capture for error scenarios.
    * @param {Object} errorInfo - Error details Info.
    * @param {Object} errorInfo.errorCode - ErrorCode to record the metrics.
    * @param {Object} errorInfo.errorMessage - Error message corresponding to errorcode to record the metrics.
    * @returns {undefined}
  */
  trackCoreMetricesError({
    errorCode,
    errorMessage
  }) {
    typeof bactm_captureCustomError === 'function' && bactm_captureCustomError(errorCode, errorMessage);
  }

  /**
    * @function toggleEnableDisableFormFields
    * @description  It will disable/enable the input and select fields and button from the given parent formelemnt.
    * @param {Object} formDetails - Object data to disable/enable the form.
    * @param {Object} formDetails.formElement - The name of the custom event.
    * @param {Boolean} formDetails.isDisabled - To update whether field need to disable or enable, on default its false.
    * @returns {undefined}
  */
  toggleEnableDisableFormFields({
    formElement,
    isDisabled = false
  }) {
    const inputElementList = formElement.querySelectorAll('input');
    const selectElementList = formElement.querySelectorAll('select');
    const buttonElementList = formElement.querySelectorAll('button');
    const formInputFields = [...inputElementList, ...selectElementList, ...buttonElementList];
    formInputFields.forEach(fieldElement => {
      fieldElement.disabled = isDisabled;
    });
  }

  /**
    * @function updateDomain
    * @description  It helps to update the domain basepath and apiEndpoints based on client payload.
    * @param {String} STORE_KEY - Store key to get the sparta store data.
    * @returns {undefined}
    * @throws Will throw error if this.basePath, this.endpoints & this.apiEndPoints are not used.
  */
  updateDomain(STORE_KEY) {
    const {
      domain
    } = spartaStoreUtility.getData(STORE_KEY);
    this.basePath = domain || this.basePath;
    this.apiEndPoints = ahCommonUtility.getEndPoints(this.endpoints, this.basePath);
    LoggerUtil.warn(`
      updateDomain function is depreciated and only do the maintain support for existing module which used.
      Passing class to common util function is not an proper approach, so its recommended to not to use it anymore.
      We are already handled the custom domain update in getEndPoints function.
    `);
  }

  /**
    * @function hbsJCRCustomUpdate
    * @description  It will overide the html based on the input object which key is consider as element id and value is consider as replacement text.
    * @param {object} modifyContent - List of Key value pair to update the DOM. Key is consider as Element ID.
    * @returns {undefined}
  */
  hbsJCRCustomUpdate(modifyContent) {
    if (modifyContent) {
      Object.keys(modifyContent).forEach(elementId => {
        const elementNode = document.querySelector(`#${elementId}`);
        if (!!elementNode) {
          elementNode.innerHTML = modifyContent[elementId];
        }
      });
    }
  }

  /**
    * @function getUpdatedJCR
    * @description  It will updated the existing InputFields, TextValues (for JS Display like Messages) and DOM content update HBS.
    * @param {object} jcrData - Complete Data jcr content which need to be used for override.
    * @returns {object}
  */
  getUpdatedJCR(jcrData) {
    const {
      authType,
      client,
      clientId
    } = this._fetchWidgetOrSiteConfigData();
    /** OrderFetch param will used for the JCR order format to get the custom JCRs objects */
    const orderFetch = [authType, client, clientId];
    let clonedJCRData = $lodash.cloneDeep(jcrData);
    if (authType || client || clientId) {
      const {
        customInputFields,
        customHbsText,
        inputsFields,
        customText
      } = clonedJCRData;
      const customSelectedInputFields = this._getCustomSuffixData({
        data: customInputFields,
        orderFetch
      });
      const customSelectedHbsText = this._getCustomSuffixData({
        data: customHbsText,
        orderFetch
      });
      const customSelectedText = this._getCustomSuffixData({
        data: customText,
        orderFetch
      });
      clonedJCRData.inputsFields = $lodash.merge(inputsFields, customSelectedInputFields);
      clonedJCRData.customHbsFlowList = customSelectedHbsText;
      clonedJCRData.text = $lodash.merge(clonedJCRData.text, customSelectedText);

      /** It will fetch the commonCustomText specific to clients and updated in commonCustomText */
      const customCommonTextKeys = Object.keys(clonedJCRData.text).filter(val => val.includes('commonCustomText_'));
      customCommonTextKeys.forEach(val => {
        clonedJCRData.customText[val] = $lodash.merge(clonedJCRData.customText[val], clonedJCRData.text[val]);
      });
      const customCommonHbsTextKeys = customSelectedHbsText ? Object.keys(customSelectedHbsText)?.filter(val => val.includes('commonHbsCustomText_')) : [];
      customCommonHbsTextKeys.forEach(val => {
        clonedJCRData.customHbsText[val] = $lodash.merge(clonedJCRData.customHbsText[val], customSelectedHbsText[val]);
      });
      return clonedJCRData;
    } else {
      LoggerUtil.error(`authType or client or clientId data are not in AhStateManagement/spartaStore Util`);
      return clonedJCRData || {};
    }
  }

  /**
    * @function _getCustomSuffixData
    * @description  It will validate the data in which order provided, try to find maximum order is present and displayed.
    * @param {Object} param0 
    * @param {object} [param0.data={}] Custom data which need to fetch based on the ordering param
    * @param {Array} [param0.orderFetch=[]] It contains the suffix order which the validation need to check
    * @returns {String | Object}
  */
  _getCustomSuffixData({
    data = {},
    orderFetch = []
  }) {
    const finalOutput = [...orderFetch].reduce((accumulator, currentValue) => {
      const keySuffix = !!accumulator.keySuffix ? accumulator.keySuffix + '_' + currentValue : currentValue;
      return {
        keySuffix,
        value: !!data[keySuffix] ? data[keySuffix] : accumulator.value
      };
    }, {
      keySuffix: '',
      value: null
    });
    return finalOutput.value;
  }

  /**
   * It will fetch Widget or Site Config data based on the widget/site loaded.
   * @returns {object}
   */
  _fetchWidgetOrSiteConfigData() {
    const widgetConfigData = this.fetchWidgetConfigData();
    const siteConfigData = this.fetchSiteConfigData();
    if (!$lodash.isEmpty(widgetConfigData)) {
      return widgetConfigData;
    } else if (!$lodash.isEmpty(siteConfigData)) {
      return siteConfigData;
    }
    return {};
  }

  /**
   * It will helps to fetch the site config details (sparta params) which added in page level
   * @returns {object}
   */
  fetchSiteConfigData() {
    const siteConfigData = spartaStoreUtility.getData(STORE_KEY.SITE_CONFIG);
    if ($lodash.isPlainObject(siteConfigData)) {
      return siteConfigData;
    }
    LoggerUtil.warn(`Kindly check whether you add the siteParam in ${STORE_KEY.SITE_CONFIG} spartaStoreUtil to fetch siteConfigData`);
    return {};
  }

  /**
    * @function behBioChangeContext
    * @description  Update the context name if BioCatch SDK is already integrated in page level.
    * @param {String} contextName - Name of the context to be updated in wup calls payload.
    * @returns {undefined}
  */
  behBioChangeContext(contextName) {
    if (typeof cdApi === 'object') {
      cdApi.changeContext(contextName);
    }
  }

  /**
    * @function isElementVisible
    * @description  It will check whether the given element is visible in the screen or not.
    * @param {HTMLElement} element - Element which need to validate.
    * @returns {boolean}
  */
  isElementVisible(element = {}) {
    const domRectObject = element.getBoundingClientRect();
    const viewPortHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
    return !(domRectObject.bottom < 0 || domRectObject.top - viewPortHeight >= 0);
  }

  /**
    * @function isElementVisible
    * @description  It will check whether the given element is visible in the screen or not.
    * @param {HTMLElement} element - Element which need to validate.
    * @returns {boolean}
  */
  scrolltoFocusableElementView(element = {}) {
    window.setTimeout(() => {
      const isElementVisible = this.isElementVisible(element);
      element.focus();
      if (isElementVisible) {
        document.querySelector('body').scrollIntoView();
      } else {
        element.scrollIntoView();
      }
    });
  }

  /**
    * @function fetchVisibleElement
    * @description  It will give the list of elements visible from the given Element List.
    * @param {Array<HTMLElement>} elementList - List of elements to be check for the visibility
    * @returns {Array<HTMLElement>}
  */
  fetchVisibleElement(elementList = []) {
    return [...elementList].filter(val => this.isElementVisible(val));
  }

  /**
    * @function getErrorCode
    * @description  It will give the errorcode based on the response or it will give default one.
    * @param {object} responseObject - Error response object
    * @returns {string}
  */
  getErrorCode(responseObject) {
    return $lodash.get(responseObject, "errorInfo.0.code");
  }

  /**
    * @function getErrorDetails
    * @description  It will give the error message based on widget config specific scenario. 
    *               If error code which provided in response not available in JCR, it will consider it as GENERAL_ERROR
    * @param {object} detailObject - Details about the JCR error list and the response
    * @param {object} detailObject.errorList - JCR error List
    * @param {object} detailObject.response - API response
    * @returns {object}
  */
  getErrorDetails({
    errorList,
    response,
    fallbackErrorCode
  }) {
    const errorCode = this.getErrorCode(response);
    const {
      clientId,
      site
    } = this._fetchWidgetOrSiteConfigData();
    if (!!errorCode) {
      const orderFetch = [errorCode, clientId, site];
      const fallbackErrorDetails = errorList[fallbackErrorCode] || {
        errorCode
      };
      return this._getCustomSuffixData({
        data: errorList,
        orderFetch
      }) || fallbackErrorDetails;
    }
    return {};
  }

  /**
    * @function displayPageError
    * @description  It will display the error message based on page or widget level depends on the param which provided.
    * @param {object} detailObject
    * @param {string} detailObject.globalId - It contains the widget level Error element ID to display widget level error
    * @param {string} detailObject.moduleId - It contains the page level Error element ID to display page level error
    * @param {object} detailObject.errorList - JCR error list which need to fetch for error display
    * @param {object} detailObject.response - API response which need to validat for the error fetch
    * @param {object} detailObject.error - If user need to display custom error message, instead of response they can pass it directly to display it
    * @param {object} detailObject.fallbackErrorCode - It will fetch the fallback error message if errorcode provided
    * @returns {undefined}
  */
  displayPageError({
    globalId,
    moduleId,
    errorList = {},
    response = {},
    error,
    fallbackErrorCode,
    option = {},
    dynamicContent
  }) {
    const {
      type,
      title,
      message,
      errorCode
    } = error ? error || {} : this.getErrorDetails({
      errorList,
      response,
      fallbackErrorCode
    });
    const elementId = type === 'fatal' ? globalId : moduleId || globalId;
    if (!!title || !!message) {
      ahMessageUtil.displayPageLevelMessaging({
        elementId,
        title,
        message,
        type: MESSAGE_TYPE.ERROR,
        option,
        dynamicContent
      });
    } else if (!!errorCode) {
      EventUtil.pub(ahConstantList.SPARTA_EVENTS.PAGE_ERROR_FALLBACK, {
        response
      });
      LoggerUtil.warn(`There is no error JCR details available to display in the elementID : ${elementId}. So trigger to Fallback event "AH_PAGE_ERROR_FALLBACK"`);
    } else {
      LoggerUtil.error(`Provided Error response is not as per the default standard to fetch error code`);
    }
  }

  /**
    * @function hidePageError
    * @description  It will hide the error message displayed based on global/module Id.
    * @param {object} detailObject
    * @param {object} detailObject.globalId - It contains the widget level Error element ID to hide widget level error
    * @param {object} detailObject.moduleId - It contains the page level Error element ID to hide page level error
    * @returns {undefined}
  */
  hidePageError({
    globalId,
    moduleId
  }) {
    ahMessageUtil.hidePageLevelMessaging(globalId);
    ahMessageUtil.hidePageLevelMessaging(moduleId);
  }

  /**
   * It will helps to fetch the widgetconfig details (autoconfig releated information like pageID, usecase etc...) which added in widget level
   * @returns {object}
   */
  fetchWidgetConfigData() {
    const widgetConfigData = spartaStoreUtility.getData(STORE_KEY.WIDGET_CONFIG);
    return $lodash.isPlainObject(widgetConfigData) ? widgetConfigData : {};
  }

  /**
   * It will helps to make JSEncrypt (RSA) encryption for the data which provided
   * @param {object} param
   * @param {string} param.key - Encryption key
   * @param {string} param.data - Input data to make encryption
   * @returns {string}
   */
  encryptData({
    key,
    data
  } = {}) {
    this.JSEncrypt.setPublicKey(key);
    return this.JSEncrypt.encrypt(data);
  }

  /**
   * @function maskData
   * @description It will generate the Phone masking based on input and we can do the customization for masking text
   * @param {string} data 
   * @param {string} prefix 
   * @returns {string}
   */
  maskData(data, prefix) {
    const defaultPrefixFormat = '***-***';
    const finalPrefixFormat = prefix ? defaultPrefixFormat.replace(/[* ]/g, prefix) : defaultPrefixFormat;
    return `${finalPrefixFormat}-${data.slice(-4)}`;
  }
  checkClient(listOfFunction = []) {
    return [...listOfFunction].some(fn => fn());
  }
  reverseFooterButtons(footerdiv, screenwidth) {
    if (window.innerWidth > screenwidth && footerdiv) {
      let btns = footerdiv.querySelectorAll(' button');
      const isButtonAlreadySwapped = footerdiv.dataset.isSwapped;
      if (!isButtonAlreadySwapped) {
        footerdiv.dataset.isSwapped = true;
        [...btns].reverse().forEach(e => {
          footerdiv.appendChild(e);
        });
      }
    }
  }

  /**
   * @function getLanguage
   * @description It will return the language which page is handling
   * @returns {string}
   */
  getLanguage() {
    return document.querySelector('html').getAttribute('lang')?.split('-')?.[0] || window?.spaParams?.language || window?.boa?.spartaAhWidgetLoader?.WIDGET_CONFIG?.language || 'en';
  }
  _removeAriaLiveInInputError(moduleSectionElement) {
    let inputElementList = moduleSectionElement.querySelectorAll('input[type="text"],input[type="password"]');
    [...inputElementList].forEach(element => element.parentElement.querySelector('.spa-input-error-message').removeAttribute('aria-live'));
  }
}

/**
 * @const AhCommonUtility
 * @description single instance of AhCommonUtility used to expose this utility
 */
const ahCommonUtility = new AhCommonUtility();
const ahLoggerUtil = LoggerUtil;
const ahMessageUtil = MessageUtil;
const ahConstant = ahConstantList;
const ahEventsUtil = EventUtil;
const ahClientCheck = ahClientCheck$1;

export { RouteNavigation, ahClientCheck, ahConstant, ahEventsUtil, LoggerCheck as ahLogRecord, ahLoggerUtil, ahMessageUtil, ahRouter, StateManagementUtil as ahStateManagementUtil, uiFrameworkUtil as ahUIFrameworkUtil, UILayerUtility as ahUILayerUtil, ahCommonUtility as default };
